import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Button, Dropdown, Tag, InlineNotification, SkeletonPlaceholder, SkeletonText, ComboBox, ProgressBar, OverflowMenu, OverflowMenuItem, Theme, RadioButtonGroup, RadioButton } from '@carbon/react';
import { ChevronLeft, ChevronRight, User, Time, Location, Add, Calendar, Close, Checkmark, Filter, TrashCan, Download } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import {
  CONCURRENCY_CONFLICT_TITLE,
  getApiErrorMessage,
  isConcurrencyConflictError,
} from '../utils/apiErrors';
import { useTheme } from '../contexts/ThemeContext';
import './AdminShiftCalendar.css';

const viewOptions = [
  { id: 'month', label: 'Mesečni' },
  { id: 'week', label: 'Nedeljni' },
  { id: 'day', label: 'Dnevni' }
];

// Backend više ne vraća SVE smene ikad: bez eksplicitnog opsega vraća samo tekući
// mesec (vidi ShiftQueryWindow na backendu). Zato kalendar mora da pošalje tačan
// opseg za ono što prikazuje — inače bi navigacija na drugi mesec prikazala prazno.
const formatDateParam = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const getVisibleRange = (view, date) => {
  if (view === 'month') {
    return {
      start: new Date(date.getFullYear(), date.getMonth(), 1),
      end: new Date(date.getFullYear(), date.getMonth() + 1, 0)
    };
  }

  if (view === 'week') {
    const start = new Date(date);
    const dayOfWeek = start.getDay();
    start.setDate(start.getDate() + (dayOfWeek === 0 ? -6 : 1 - dayOfWeek));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
  }

  // Dnevni prikaz
  return { start: new Date(date), end: new Date(date) };
};

const AdminShiftCalendar = () => {
  const { theme } = useTheme();
  
  // Load saved state from localStorage
  const getSavedState = () => {
    try {
      const savedState = localStorage.getItem('shiftManagementState');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        return {
          // Uvek prikaži mesečni prikaz pri ulasku na tab
          view: 'month',
          // Uvek koristi trenutni datum, ne čuvaj prethodno izabrani mesec
          currentDate: new Date(),
          // Sačuvaj izabrano područje i zaposlenog
          filters: parsed.filters || { area: '', employeeId: '' }
        };
      }
    } catch (error) {
      console.error('Error loading saved state:', error);
    }
    return {
      view: 'month',
      currentDate: new Date(),
      filters: { area: '', employeeId: '' }
    };
  };

  const savedState = getSavedState();
  
  const [view, setView] = useState(savedState.view);
  const [currentDate, setCurrentDate] = useState(savedState.currentDate);
  const [selectedDates, setSelectedDates] = useState([]);
  const [isButtonExiting, setIsButtonExiting] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [areas, setAreas] = useState([]);

  // Štiti od trke: pri brzoj navigaciji kroz mesece stariji odgovor ne sme da pregazi noviji.
  const shiftsRequestRef = useRef(0);
  
  const [filters, setFilters] = useState(savedState.filters);

  const [isSchedulingMode, setIsSchedulingMode] = useState(false);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedShiftType, setSelectedShiftType] = useState(null);
  const [isCreatingShifts, setIsCreatingShifts] = useState(false);
  const [schedulingStep, setSchedulingStep] = useState(0); // 0: select shift type, 1: creating
  const [successMessage, setSuccessMessage] = useState(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFilterExiting, setIsFilterExiting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [isDeletingShift, setIsDeletingShift] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  // Report side panel state (weekly + monthly)
  const [isReportPanelOpen, setIsReportPanelOpen] = useState(false);
  const [reportType, setReportType] = useState('weekly'); // 'weekly' | 'monthly'
  const [reportAreaId, setReportAreaId] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  useEffect(() => {
    fetchInitialData();
    fetchEmployees();
    // fetchShifts se poziva iz efekta ispod (zavisi od filtera i vidljivog opsega),
    // pa se ovde ne poziva — inače bi se na mount-u poslala dva identična zahteva.
  }, []);

  // Ponovo učitaj kada se promeni vidljivi opseg (navigacija kroz mesece/nedelje/dane)
  // ili filteri — backend vraća samo traženi prozor, ne celu istoriju smena.
  useEffect(() => {
    fetchShifts();
  }, [filters, areas, view, currentDate]);

  // Ako sačuvani filter područja više ne postoji, resetuj ga da se smene odmah prikažu.
  useEffect(() => {
    if (areas.length > 0 && filters.area && !areas.some(a => a.id === filters.area)) {
      setFilters(prev => ({ ...prev, area: '' }));
    }
  }, [areas, filters.area]);

  // Ako sačuvani filter zaposlenog više ne postoji, resetuj ga da se smene odmah prikažu.
  useEffect(() => {
    if (employees.length > 0 && filters.employeeId && !employees.some(e => e.id === filters.employeeId)) {
      setFilters(prev => ({ ...prev, employeeId: '' }));
    }
  }, [employees, filters.employeeId]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave = {
      view,
      currentDate: currentDate.toISOString(),
      filters
    };
    localStorage.setItem('shiftManagementState', JSON.stringify(stateToSave));
  }, [view, currentDate, filters]);

  const fetchInitialData = async () => {
    try {
      const response = await axiosInstance.get('/api/Area');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.data || response.data?.value || response.data?.$values || []);

      const areaOptions = data.map(area => ({
        id: area.id || area,
        label: area.name || area
      }));
      setAreas(areaOptions);
    } catch (err) {
      setError('Greška pri učitavanju područja');
      console.error('Error fetching areas:', err);
    }
  };

  const fetchShifts = async () => {
    const requestId = ++shiftsRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      // Area filter očekuje naziv oblasti (string), ne ID.
      // Ako područja još nisu učitana, ne šalji filter područja kako bi se
      // smene odmah prikazale (bez čekanja na ručni odabir filtera).
      if (filters.area) {
        const areaName = areas.find(a => a.id === filters.area)?.label;
        if (areaName) params.Area = areaName;
      }
      if (filters.employeeId) params.EmployeeId = filters.employeeId;

      // Uvek šalji opseg koji kalendar prikazuje (backend ne vraća celu istoriju).
      const range = getVisibleRange(view, currentDate);
      params.StartDate = formatDateParam(range.start);
      params.EndDate = formatDateParam(range.end);

      const response = await axiosInstance.get('/api/Shift', { params });

      // Stariji zahtev je u međuvremenu prevaziđen — ignoriši njegov odgovor.
      if (requestId !== shiftsRequestRef.current) return;

      if (response.data.success) {
        setShifts(response.data.data.shifts);
      }
    } catch (err) {
      if (requestId !== shiftsRequestRef.current) return;
      setError('Greška pri učitavanju smena');
      console.error('Error fetching shifts:', err);
    } finally {
      if (requestId === shiftsRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get('/api/Employee');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.data || response.data?.value || response.data?.$values || []);

      const employeeOptions = data
        .map(u => ({
          id: u.id,
          label: `${u.fullName || `${u.firstName} ${u.lastName}`}${u.employeeTypeName ? ` (${u.employeeTypeName})` : ''}`,
          fullName: u.fullName || `${u.firstName} ${u.lastName}`
        }));
      setEmployees(employeeOptions);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchShiftTypes = async () => {
    try {
      const response = await axiosInstance.get('/api/ShiftType');
      if (response.data.success) {
        setShiftTypes(response.data.data.shiftTypesByArea);
      }
    } catch (err) {
      setError('Greška pri učitavanju tipova smena');
      console.error('Error fetching shift types:', err);
    }
  };

  const handleScheduleShifts = async () => {
    if (!selectedShiftType || !filters.employeeId) {
      setError('Molimo izaberite tip smene');
      return;
    }

    setSchedulingStep(1);
    setIsCreatingShifts(true);
    
    try {
      const formattedDates = selectedDates.map(date => formatDateKey(date));
      const response = await axiosInstance.post('/api/Shift', {
        shiftTypeId: selectedShiftType,
        employeeId: filters.employeeId,
        dates: formattedDates
      });

      if (response.data.success) {
        // Close modal and show success notification
        setIsSchedulingMode(false);
        setSelectedDates([]);
        setSelectedArea(null);
        setSelectedShiftType(null);
        setSchedulingStep(0);
        setSuccessMessage('Smene su uspešno zakazane!');
        setTimeout(() => setSuccessMessage(null), 5000);
        fetchShifts();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Greška pri kreiranju smena'));
      console.error('Error creating shifts:', err);
      setSchedulingStep(0);
    } finally {
      setIsCreatingShifts(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      area: '',
      employeeId: ''
    });
  };

  const handleCloseMobileFilter = () => {
    setIsFilterExiting(true);
    setTimeout(() => {
      setIsMobileFilterOpen(false);
      setIsFilterExiting(false);
    }, 300);
  };

  // Get the Monday of the week containing the given date
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? 6 : day - 1; // Monday = 0, Sunday = 6
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Get the first day of the month containing the given date
  const getMonthStart = (date) => {
    const d = new Date(date);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const openReportPanel = (type = 'weekly') => {
    setReportType(type);
    setReportAreaId('');
    setReportError(null);
    setIsReportPanelOpen(true);
  };

  const closeReportPanel = () => {
    setIsReportPanelOpen(false);
    setReportError(null);
  };

  const handleGenerateReport = async () => {
    setReportError(null);
    setIsGeneratingReport(true);
    try {
      const params = new URLSearchParams();
      let endpoint;
      let filename;

      if (reportType === 'monthly') {
        const monthStart = getMonthStart(currentDate);
        const monthTimestamp = `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}-${String(monthStart.getDate()).padStart(2, '0')}`;
        params.append('monthTimestamp', monthTimestamp);
        endpoint = '/api/Shift/report/monthly';
        filename = `izvestaj_smene_${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}.pdf`;
      } else {
        const weekStart = getWeekStart(currentDate);
        const weekTimestamp = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
        params.append('weekTimestamp', weekTimestamp);
        endpoint = '/api/Shift/report';
        filename = `izvestaj_smene_${weekTimestamp}.pdf`;
      }

      if (reportAreaId) {
        params.append('areaId', reportAreaId);
      }

      const response = await axiosInstance.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });

      // Trigger file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setIsReportPanelOpen(false);
      setNotifications(prev => [...prev, {
        id: Date.now(),
        kind: 'success',
        title: 'Uspeh',
        subtitle: reportType === 'monthly'
          ? 'Mesečni izveštaj je uspešno generisan'
          : 'Nedeljni izveštaj je uspešno generisan'
      }]);
    } catch (error) {
      console.error('Error generating report:', error);
      setReportError(error.response?.data?.message || 'Greška pri generisanju izveštaja');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getMonthName = (date) => {
    const months = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
    return months[date.getMonth()];
  };

  const getDayName = (date) => {
    const days = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
    return days[date.getDay()];
  };

  const formatDateLong = (date) => {
    return new Intl.DateTimeFormat('sr-Latn', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const getShortDayName = (date) => {
    const days = ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'];
    return days[date.getDay()];
  };

  const getShiftsForDate = (date) => {
    const dateKey = formatDateKey(date);
    return shifts.filter(shift => {
      // Backend šalje datum bez vremenske zone (npr. "2026-09-30T06:00:00").
      // Čitamo datum direktno iz stringa kako bismo smenu prikazali na dan koji je backend odredio.
      const match = String(shift.startTime).match(/^(\d{4})-(\d{2})-(\d{2})/);
      const shiftDateKey = match
        ? `${match[1]}-${match[2]}-${match[3]}`
        : formatDateKey(new Date(shift.startTime));
      return shiftDateKey === dateKey;
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    // Backend šalje vreme bez vremenske zone (npr. "2026-09-30T06:00:00").
    // Čitamo sate i minute direktno iz stringa, bez konverzije u lokalnu/UTC vremensku zonu.
    const match = String(dateString).match(/T(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const translateStatus = (status) => {
    const statusMap = {
      'planned': 'Planirana',
      'in-progress': 'U toku',
      'completed': 'Završena'
    };
    return statusMap[status] || status;
  };

  const handleDeleteShift = async () => {
    if (!shiftToDelete) return;
    
    setIsDeletingShift(true);
    try {
      const response = await axiosInstance.delete(`/api/Shift/${shiftToDelete.id}`);
      
      if (response.data.success) {
        // Show success notification
        const newNotification = {
          id: Date.now(),
          kind: 'success',
          title: 'Uspeh',
          subtitle: 'Smena je uspešno obrisana',
          isExiting: false
        };
        setNotifications(prev => [...prev, newNotification]);
        
        // Auto remove after 5 seconds
        setTimeout(() => {
          setNotifications(prev => 
            prev.map(n => n.id === newNotification.id ? { ...n, isExiting: true } : n)
          );
          setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
          }, 300);
        }, 5000);
        
        // Refresh shifts
        fetchShifts();
        
        // Close modal
        setDeleteModalOpen(false);
        setShiftToDelete(null);
        
        // Close the day side panel so the calendar is visible again
        setSelectedDay(null);
      }
    } catch (err) {
      console.error('Error deleting shift:', err);
      
      // Show error notification
      if (isConcurrencyConflictError(err)) {
        // Smena je u međuvremenu izmenjena — osveži kalendar da prikaz bude tačan.
        fetchShifts();
      }

      const errorMessage = getApiErrorMessage(err, 'Smena ne postoji ili ne može biti obrisana');
      const newNotification = {
        id: Date.now(),
        kind: 'error',
        title: isConcurrencyConflictError(err) ? CONCURRENCY_CONFLICT_TITLE : 'Greška',
        subtitle: errorMessage,
        isExiting: false
      };
      setNotifications(prev => [...prev, newNotification]);
      
      // Auto remove after 5 seconds
      setTimeout(() => {
        setNotifications(prev => 
          prev.map(n => n.id === newNotification.id ? { ...n, isExiting: true } : n)
        );
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
        }, 300);
      }, 5000);
    } finally {
      setIsDeletingShift(false);
    }
  };

  const translateRole = (role) => {
    const roleMap = {
      'nurse': 'Sestra',
      'caretaker': 'Negovateljica',
      'kitchen': 'Kuhinja',
      'administrator': 'Administrator'
    };
    return roleMap[role] || role;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planned': return 'blue';
      case 'in-progress': return 'green';
      case 'completed': return 'gray';
      default: return 'purple';
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (view === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Month View
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const tempDate = new Date(year, month, 1);

    while (tempDate.getMonth() === month) {
      const dateShifts = getShiftsForDate(tempDate);
      const isToday = formatDateKey(tempDate) === formatDateKey(new Date());

      days.push({
        date: new Date(tempDate),
        dateKey: formatDateKey(tempDate),
        isCurrentMonth: true,
        isToday,
        shifts: dateShifts
      });

      tempDate.setDate(tempDate.getDate() + 1);
    }
    
    // Calculate offset for first day (0 = Monday, 6 = Sunday)
    const firstDayOfWeek = firstDay.getDay();
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Create day names array rotated based on first day
    const allDays = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];
    const rotatedDays = [...allDays.slice(offset), ...allDays.slice(0, offset)];

    return (
      <div className="calendar-month-view">
        <div className="calendar-month-header">
          {rotatedDays.map(day => (
            <div key={day} className="calendar-weekday-label">{day}</div>
          ))}
        </div>
        <div className="calendar-month-grid">
          {days.map((day, idx) => {
            const dateKey = formatDateKey(day.date);
            const isSelected = selectedDates.some(d => formatDateKey(d) === dateKey);
            
            // Check if date is in the past or today
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const cellDate = new Date(day.date);
            cellDate.setHours(0, 0, 0, 0);
            const isPastOrToday = cellDate <= today;
            
            return (
            <div
              key={idx}
              className={`calendar-day-cell ${day.isToday ? 'today' : ''} ${day.shifts.length > 0 ? 'has-shifts' : ''} ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                if (filters.employeeId && day.shifts.length === 0 && !isPastOrToday) {
                  if (isSelected) {
                    const newDates = selectedDates.filter(d => formatDateKey(d) !== dateKey);
                    if (newDates.length === 0) {
                      setIsButtonExiting(true);
                      setTimeout(() => {
                        setSelectedDates(newDates);
                        setIsButtonExiting(false);
                      }, 300);
                    } else {
                      setSelectedDates(newDates);
                    }
                  } else {
                    setIsButtonExiting(false);
                    setSelectedDates([...selectedDates, day.date]);
                  }
                }
                setCurrentDate(day.date);
                if (day.shifts.length > 0) {
                  setSelectedDay(day);
                }
              }}
            >
              <div className="calendar-day-number">
                {day.date.getDate()}
              </div>
              {day.shifts.length === 1 && (
                <button
                  className="shift-delete-btn-month"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShiftToDelete(day.shifts[0]);
                    setDeleteModalOpen(true);
                  }}
                  title="Obriši smenu"
                >
                  <TrashCan size={16} />
                </button>
              )}
              <div className="calendar-day-shifts-count">
                {day.shifts.length > 0 ? (
                  <span className="shift-count-text" style={{ fontSize: '0.75rem', fontWeight: '500', color: '#9fb746' }}>
                    <span className="shift-count-number">{day.shifts.length}</span>
                    <span className="shift-count-label"> {day.shifts.length === 1 ? 'smena' : day.shifts.length < 5 ? 'smene' : 'smena'}</span>
                  </span>
                ) : filters.employeeId && !isPastOrToday ? (
                  <div className="calendar-add-shift-btn">
                    {isSelected ? (
                      <Checkmark size={20} />
                    ) : (
                      <Add size={20} />
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          );
          })}
        </div>
      </div>
    );
  };

  // Week View
  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = currentDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfWeek.setDate(currentDate.getDate() + diff);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDays.push({
        date,
        dateKey: formatDateKey(date),
        isToday: formatDateKey(date) === formatDateKey(new Date()),
        shifts: getShiftsForDate(date)
      });
    }

    return (
      <div className="calendar-week-view">
        <div className="calendar-week-header">
          {weekDays.map((day, idx) => (
            <div key={idx} className={`calendar-week-day-header ${day.isToday ? 'today' : ''}`}>
              <div className="week-day-name">{getShortDayName(day.date)}</div>
              <div className="week-day-number">{day.date.getDate()}</div>
            </div>
          ))}
        </div>
        <div className="calendar-week-grid">
          {weekDays.map((day, idx) => {
            const isDateSelected = selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date));
            
            // Check if date is in the past or today
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const cellDate = new Date(day.date);
            cellDate.setHours(0, 0, 0, 0);
            const isPastOrToday = cellDate <= today;
            
            return (
            <div
              key={idx}
              className={`calendar-week-day-column ${isDateSelected ? 'date-selected' : ''}`}
              onClick={() => {
                if (day.shifts.length > 0) {
                  setCurrentDate(day.date);
                  setSelectedDay(day);
                }
              }}
            >
              <div className="mobile-week-day-header">
                <span className="mobile-week-day-name">{getShortDayName(day.date)}</span>
                <span className="mobile-week-day-date">{day.date.getDate()} {getMonthName(day.date)}</span>
              </div>
              {day.shifts.length === 0 && filters.employeeId && !isPastOrToday ? (
                <>
                  <div className="add-shift-btn-wrapper">
                    <div 
                      className="calendar-add-shift-btn week-add-shift-btn" 
                      style={{ 
                        backgroundColor: selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date)) ? '#9fb746' : 'rgba(159, 183, 70, 0.15)',
                        border: '2px solid #9fb746',
                        color: selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date)) ? '#ffffff' : '#9fb746'
                      }}
                      onClick={() => {
                        const dateKey = formatDateKey(day.date);
                        const isSelected = selectedDates.some(d => formatDateKey(d) === dateKey);
                        
                        if (isSelected) {
                          setSelectedDates(selectedDates.filter(d => formatDateKey(d) !== dateKey));
                        } else {
                          setSelectedDates([...selectedDates, day.date]);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date))) {
                          e.currentTarget.style.backgroundColor = 'rgba(159, 183, 70, 0.25)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date))) {
                          e.currentTarget.style.backgroundColor = 'rgba(159, 183, 70, 0.15)';
                        }
                      }}
                    >
                      {selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date)) ? (
                        <Checkmark size={24} />
                      ) : (
                        <Add size={24} />
                      )}
                    </div>
                  </div>
                  <div className="free-day-message">
                    Slobodan dan
                    <div className="mobile-add-shift-btn">
                      <button
                        className={`mobile-week-add-btn ${selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date)) ? 'selected' : ''}`}
                        onClick={() => {
                          const dateKey = formatDateKey(day.date);
                          const isSelected = selectedDates.some(d => formatDateKey(d) === dateKey);
                          
                          if (isSelected) {
                            setSelectedDates(selectedDates.filter(d => formatDateKey(d) !== dateKey));
                          } else {
                            setSelectedDates([...selectedDates, day.date]);
                          }
                        }}
                      >
                        {selectedDates.some(d => formatDateKey(d) === formatDateKey(day.date)) ? (
                          <Checkmark size={20} />
                        ) : (
                          <Add size={20} />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : day.shifts.length === 0 && !filters.employeeId ? (
                <div className="free-day-message">Slobodan dan</div>
              ) : null}
              {day.shifts.map((shift, shiftIdx) => (
                <div key={shiftIdx} className="calendar-week-shift-card">
                  <div className="shift-card-compact">
                    <div className="shift-card-compact-main">
                      <span className="shift-area-role">{formatTime(shift.startTime)} - {formatTime(shift.endTime)}</span>
                      <span className="shift-employee-name">{shift.employeeFullName}</span>
                    </div>
                  </div>
                  <div className="shift-card-expanded">
                    <div className="shift-card-header">
                      <span className="shift-card-time">
                        {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                      </span>
                      <button
                        className="shift-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShiftToDelete(shift);
                          setDeleteModalOpen(true);
                        }}
                        title="Obriši smenu"
                      >
                        <TrashCan size={16} />
                      </button>
                    </div>
                    <div className="shift-card-title">{shift.name}</div>
                    <div className="shift-card-employee">
                      <User size={16} />
                      <span>{shift.employeeFullName}</span>
                    </div>
                    <div className="shift-card-area">{shift.area}</div>
                  </div>
                </div>
              ))}
            </div>
          );
          })}
        </div>
      </div>
    );
  };

  // Day View
  const renderDayView = () => {
    const dayShifts = getShiftsForDate(currentDate);

    return (
      <div className="calendar-day-view">
        <div className="calendar-day-shifts-list">
          {dayShifts.length === 0 ? (
            <div className="no-shifts-message">
              <Time size={32} />
              <p>Nema smena za ovaj dan</p>
            </div>
          ) : (
            dayShifts.map((shift, idx) => (
              <div key={idx} className="calendar-day-shift-card">
                <div className="shift-card-time-indicator">
                  <div className="time-dot" />
                  <div className="time-line" />
                </div>
                <div className="shift-card-content">
                  <div className="shift-card-header">
                    <div className="shift-card-title-row">
                      <h5>{shift.area}</h5>
                      <button
                        className="shift-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShiftToDelete(shift);
                          setDeleteModalOpen(true);
                        }}
                        title="Obriši smenu"
                      >
                        <TrashCan size={16} />
                      </button>
                    </div>
                    <div className="shift-card-time-range">
                      <Time size={16} />
                      {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                    </div>
                  </div>
                  <div className="shift-card-details">
                    <div className="shift-detail-row">
                      <User size={16} />
                      <span className="shift-detail-label">Zaposleni:</span>
                      <span className="shift-detail-value">{shift.employeeFullName}</span>
                      <Tag size="sm" type="outline">{translateRole(shift.employeeRole)}</Tag>
                    </div>
                    <div className="shift-detail-row">
                      <Location size={16} />
                      <span className="shift-detail-label">Tip smene:</span>
                      <span className="shift-detail-value">{shift.name}</span>
                    </div>
                    {shift.description && (
                      <div className="shift-description">
                        <span className="shift-detail-label">Opis:</span>
                        <p>{shift.description}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <>
    {error && ReactDOM.createPortal(
      <div className="floating-notification-wrapper" data-carbon-theme={theme}>
        <InlineNotification
          kind={error.includes('današnji datum') ? 'warning' : 'error'}
          title={error.includes('današnji datum') ? 'Upozorenje' : 'Greška'}
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          lowContrast={true}
        />
      </div>,
      document.body
    )}
    {successMessage && ReactDOM.createPortal(
      <div className="floating-notification-wrapper" data-carbon-theme={theme}>
        <InlineNotification
          kind="success"
          title="Uspeh"
          subtitle={successMessage}
          onCloseButtonClick={() => setSuccessMessage(null)}
          lowContrast={true}
        />
      </div>,
      document.body
    )}
    <div className="admin-shift-calendar">

      {/* Header */}
      <div className="calendar-header">
        <div className="calendar-header-left">
          <h3 className="calendar-title">
            {view === 'month' && `${getMonthName(currentDate)} ${currentDate.getFullYear()}`}
            {view === 'week' && `Nedelja ${currentDate.getDate()} ${getMonthName(currentDate)}`}
            {view === 'day' && `${getDayName(currentDate)}, ${currentDate.getDate()} ${getMonthName(currentDate)}`}
          </h3>
        </div>
        <div className="calendar-header-right desktop-filters">
          {areas.length > 0 && (
            <Dropdown
              id="area-filter"
              size="sm"
              titleText=""
              label="Područje"
              items={[{ id: '', label: 'Sva područja' }, ...areas]}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={filters.area ? areas.find(a => a.id === filters.area) : { id: '', label: 'Sva područja' }}
              onChange={({ selectedItem }) => setFilters({ ...filters, area: selectedItem ? selectedItem.id : '' })}
            />
          )}

          {employees.length > 0 && (
            <ComboBox
              id="employee-filter"
              size="sm"
              titleText=""
              placeholder="Svi zaposleni"
              items={[{ id: '', label: 'Svi zaposleni' }, ...employees]}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={filters.employeeId ? employees.find(e => e.id === filters.employeeId) : { id: '', label: 'Svi zaposleni' }}
              onChange={({ selectedItem }) => setFilters({ ...filters, employeeId: selectedItem ? selectedItem.id : '' })}
            />
          )}

          <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--cds-border-subtle, #e0e0e0)', margin: '0 0.5rem' }} />
          
          <div className="calendar-nav-group">
            <Button
              kind="ghost"
              size="sm"
              renderIcon={ChevronLeft}
              onClick={() => navigateDate(-1)}
              hasIconOnly
              iconDescription="Prethodni"
            />
            <Button
              kind="ghost"
              size="sm"
              onClick={goToToday}
            >
              Danas
            </Button>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={ChevronRight}
              onClick={() => navigateDate(1)}
              hasIconOnly
              iconDescription="Sledeći"
            />
            <div className="calendar-view-selector">
              <Dropdown
                id="view-selector"
                size="sm"
                titleText=""
                label=""
                items={viewOptions}
                itemToString={(item) => (item ? item.label : '')}
                selectedItem={viewOptions.find(option => option.id === view)}
                onChange={({ selectedItem }) => setView(selectedItem.id)}
              />
            </div>
          </div>

          <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--cds-border-subtle, #e0e0e0)', margin: '0 0.5rem' }} />

          <OverflowMenu size="sm" flipped className="report-overflow-menu">
            <OverflowMenuItem
              itemText="Nedeljni izveštaj"
              onClick={() => openReportPanel('weekly')}
            />
            <OverflowMenuItem
              itemText="Mesečni izveštaj"
              onClick={() => openReportPanel('monthly')}
            />
          </OverflowMenu>
        </div>
      </div>

      {/* Filters Row - removed */}
      <div className="calendar-filters-row" style={{ display: 'none' }}>
        {areas.length > 0 && (
          <Dropdown
            id="area-filter"
            size="sm"
            titleText=""
            label="Područje"
            items={[{ id: '', label: 'Sva područja' }, ...areas]}
            itemToString={(item) => (item ? item.label : '')}
            selectedItem={areas.find(a => a.id === filters.area) || { id: '', label: 'Sva područja' }}
            onChange={({ selectedItem }) => setFilters({ ...filters, area: selectedItem.id })}
          />
        )}

        {employees.length > 0 && (
          <Dropdown
            id="employee-filter"
            size="sm"
            titleText=""
            label="Zaposleni"
            items={[{ id: '', label: 'Svi zaposleni' }, ...employees]}
            itemToString={(item) => (item ? item.label : '')}
            selectedItem={employees.find(e => e.id === filters.employeeId) || { id: '', label: 'Svi zaposleni' }}
            onChange={({ selectedItem }) => setFilters({ ...filters, employeeId: selectedItem.id })}
          />
        )}

        {areas.length > 0 && (
          <Dropdown
            id="area-filter-hidden"
            size="sm"
            titleText=""
            label="Područje"
            items={areas}
            itemToString={(item) => (item ? item.label : '')}
            selectedItem={areas.find(a => a.id === filters.area) || areas[0]}
            onChange={({ selectedItem }) => setFilters({ ...filters, area: selectedItem.id })}
          />
        )}
      </div>

      {/* Calendar Content */}
      <div className="calendar-content">
        {loading ? (
          <div className="calendar-skeleton">
            {view === 'month' && (
              <div className="calendar-month-view">
                <div className="calendar-month-header">
                  {['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'].map(day => (
                    <div key={day} className="calendar-weekday-label">{day}</div>
                  ))}
                </div>
                <div className="calendar-month-grid">
                  {Array.from({ length: 35 }).map((_, idx) => (
                    <SkeletonPlaceholder key={idx} className="calendar-day-skeleton" />
                  ))}
                </div>
              </div>
            )}
            {view === 'week' && (
              <div className="calendar-week-view">
                <div className="calendar-week-header">
                  {Array.from({ length: 7 }).map((_, idx) => (
                    <SkeletonPlaceholder key={idx} style={{ height: '60px' }} />
                  ))}
                </div>
                <div className="calendar-week-grid" style={{ paddingRight: '1rem', paddingBottom: '1rem' }}>
                  {Array.from({ length: 7 }).map((_, idx) => (
                    <div key={idx} className="calendar-week-day-column" style={{ gap: '0.35rem', padding: '0.35rem 0.2rem 2.5rem 0.2rem' }}>
                      <SkeletonPlaceholder style={{ height: '60px', marginBottom: '0' }} />
                      <SkeletonPlaceholder style={{ height: '60px', marginBottom: '0' }} />
                      <SkeletonPlaceholder style={{ height: '60px', marginBottom: '0' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {view === 'day' && (
              <div className="calendar-day-view">
                <div className="calendar-day-shifts-list">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <SkeletonPlaceholder key={idx} style={{ height: '60px', marginBottom: '1rem' }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {view === 'month' && renderMonthView()}
            {view === 'week' && renderWeekView()}
            {view === 'day' && renderDayView()}
          </>
        )}
      </div>

      {/* Mobile report buttons - shown below the calendar on mobile */}
      <div className="mobile-report-buttons">
        <Button
          kind="tertiary"
          size="md"
          renderIcon={Download}
          onClick={() => openReportPanel('weekly')}
        >
          Nedeljni izveštaj
        </Button>
        <Button
          kind="tertiary"
          size="md"
          renderIcon={Download}
          onClick={() => openReportPanel('monthly')}
        >
          Mesečni izveštaj
        </Button>
      </div>
    </div>

    {/* Floating Schedule Button - Rendered via Portal */}
    {(selectedDates.length > 0 || isButtonExiting) && ReactDOM.createPortal(
      <button
        className={`floating-button mobile-filter-floating-btn floating-schedule-btn ${isButtonExiting ? 'floating-schedule-btn-exit' : ''}`}
        onClick={() => {
          setIsSchedulingMode(true);
          // Set selected area from filters if available
          if (filters.area) {
            setSelectedArea(filters.area);
          }
          fetchShiftTypes();
        }}
        style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          height: '56px',
          padding: '0 24px',
          backgroundColor: '#9fb746',
          border: 'none',
          borderRadius: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          gap: '12px',
          outline: 'none',
          zIndex: 9999
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#8da63d';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#9fb746';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
        }}
      >
        <Calendar 
          size={24}
          style={{
            flexShrink: 0,
            minWidth: '24px',
            minHeight: '24px',
            fill: '#ffffff'
          }}
          aria-label="Kalendar"
          color="#ffffff"
        />
        <span style={{
          color: 'white',
          fontWeight: '500',
          fontSize: '0.95rem',
          whiteSpace: 'nowrap',
          userSelect: 'none'
        }}>
          Zakaži smene ({selectedDates.length})
        </span>
      </button>,
      document.body
    )}

    {/* Mobile Filter Button */}
    {ReactDOM.createPortal(
      <button
        className="floating-button mobile-filter-floating-btn"
        onClick={() => setIsMobileFilterOpen(true)}
        style={{
          position: 'fixed',
          bottom: '2rem',
          left: '2rem',
          height: '56px',
          padding: '0 24px',
          backgroundColor: '#9fb746',
          border: 'none',
          borderRadius: '28px',
          display: 'none',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          gap: '12px',
          outline: 'none',
          zIndex: 9998
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#8da63d';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#9fb746';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
        }}
      >
        <Filter 
          size={24}
          style={{
            flexShrink: 0,
            minWidth: '24px',
            minHeight: '24px',
            fill: '#ffffff'
          }}
          aria-label="Filteri"
          color="#ffffff"
        />
        <span style={{
          color: '#ffffff',
          fontWeight: '500',
          fontSize: '0.95rem',
          whiteSpace: 'nowrap',
          userSelect: 'none'
        }}>
          Filteri
        </span>
      </button>,
      document.body
    )}

    {/* Mobile Filter Modal */}
    {isMobileFilterOpen && ReactDOM.createPortal(
      <div 
        className={isFilterExiting ? 'filter-modal-exiting' : ''}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme === 'g90' || theme === 'g100' ? 'rgba(22, 22, 22, 0.85)' : 'rgba(244, 244, 244, 0.85)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'flex-end',
          animation: isFilterExiting ? 'fadeOut 0.2s ease-out' : 'fadeIn 0.2s ease-out'
        }}
        onClick={handleCloseMobileFilter}
      >
        <div 
          className="mobile-filter-panel"
          data-carbon-theme={theme}
          style={{
            width: '100%',
            backgroundColor: theme === 'g90' || theme === 'g100' ? '#262626' : '#ffffff',
            borderRadius: '16px 16px 0 0',
            padding: '1.5rem',
            animation: isFilterExiting ? 'slideDownOut 0.3s ease-out' : 'slideUpIn 0.3s ease-out',
            maxHeight: '70vh',
            overflowY: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500', color: theme === 'g90' || theme === 'g100' ? '#ffffff' : '#161616' }}>Filteri</h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              className="mobile-filter-close-btn"
              renderIcon={Close}
              iconDescription="Zatvori"
              onClick={handleCloseMobileFilter}
              style={{ color: theme === 'g90' || theme === 'g100' ? '#ffffff' : '#161616' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'nowrap', width: '100%' }}>
              <Button
                kind="primary"
                size="sm"
                renderIcon={ChevronLeft}
                onClick={() => navigateDate(-1)}
                hasIconOnly
                iconDescription="Prethodni"
                style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', flex: 1, minWidth: '48px' }}
              />
              <Button
                kind="primary"
                size="sm"
                className="mobile-today-btn"
                onClick={goToToday}
                style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', whiteSpace: 'nowrap', flex: 1, justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
              >
                Danas
              </Button>
              <Button
                kind="primary"
                size="sm"
                renderIcon={ChevronRight}
                onClick={() => navigateDate(1)}
                hasIconOnly
                iconDescription="Sledeći"
                style={{ backgroundColor: '#9fb746', borderColor: '#9fb746', flex: 1, minWidth: '48px' }}
              />
            </div>

            <div style={{ textAlign: 'center', fontSize: '0.875rem', fontWeight: '500', color: theme === 'g90' || theme === 'g100' ? '#ffffff' : '#161616' }}>
              {view === 'month' && `${getMonthName(currentDate)} ${currentDate.getFullYear()}`}
              {view === 'week' && `Nedelja ${currentDate.getDate()} ${getMonthName(currentDate)}`}
              {view === 'day' && `${getDayName(currentDate)}, ${currentDate.getDate()} ${getMonthName(currentDate)}`}
            </div>

            <Dropdown
              id="mobile-view-selector"
              size="md"
              titleText="Prikaz"
              label="Prikaz"
              items={viewOptions}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={viewOptions.find(option => option.id === view)}
              onChange={({ selectedItem }) => setView(selectedItem.id)}
            />

            {areas.length > 0 && (
              <Dropdown
                id="mobile-area-filter"
                size="md"
                titleText="Područje"
                label="Područje"
                items={[{ id: '', label: 'Sva područja' }, ...areas]}
                itemToString={(item) => (item ? item.label : '')}
                selectedItem={filters.area ? areas.find(a => a.id === filters.area) : { id: '', label: 'Sva područja' }}
                onChange={({ selectedItem }) => setFilters({ ...filters, area: selectedItem ? selectedItem.id : '' })}
              />
            )}

            {employees.length > 0 && (
              <ComboBox
                id="mobile-employee-filter"
                size="md"
                titleText="Zaposleni"
                placeholder="Svi zaposleni"
                items={[{ id: '', label: 'Svi zaposleni' }, ...employees]}
                itemToString={(item) => (item ? item.label : '')}
                selectedItem={filters.employeeId ? employees.find(e => e.id === filters.employeeId) : { id: '', label: 'Svi zaposleni' }}
                onChange={({ selectedItem }) => setFilters({ ...filters, employeeId: selectedItem ? selectedItem.id : '' })}
              />
            )}

            <Button
              kind="primary"
              size="md"
              style={{ width: '100%', marginTop: '1rem' }}
              onClick={handleCloseMobileFilter}
            >
              Primeni filtere
            </Button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* Scheduling Mode Overlay */}
    {isSchedulingMode && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9998,
        animation: 'fadeIn 0.3s ease-out'
      }} onClick={() => {
        if (!isCreatingShifts) {
          setIsSchedulingMode(false);
          setSelectedShiftType(null);
          setSchedulingStep(0);
        }
      }} />
    )}

    {/* Scheduling Panel in Calendar Area */}
    {isSchedulingMode && ReactDOM.createPortal(
      <>
        <div 
          className="scheduling-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => {
            if (!isCreatingShifts) {
              setIsSchedulingMode(false);
              setSelectedShiftType(null);
              setSchedulingStep(0);
              setSelectedArea(null);
            }
          }}
        />
        <div className="scheduling-panel" style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '420px',
        maxWidth: '100%',
        backgroundColor: theme === 'g90' || theme === 'g100' ? '#262626' : '#ffffff',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.2)',
        zIndex: 9999,
        animation: 'slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--cds-border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ 
              margin: 0, 
              fontSize: '1.25rem', 
              fontWeight: '500',
              color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616'
            }}>
              Zakaži smene
            </h3>
            <p style={{ 
              margin: '0.25rem 0 0 0', 
              fontSize: '0.875rem', 
              color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252'
            }}>
              {selectedDates.length} {selectedDates.length === 1 ? 'datum' : 'datuma'} izabrano
            </p>
          </div>
          {!isCreatingShifts && (
            <div style={{
              filter: theme === 'g90' || theme === 'g100' ? 'invert(1) brightness(2)' : 'none'
            }}>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Close}
                iconDescription="Zatvori"
                onClick={() => {
                  setIsSchedulingMode(false);
                  setSelectedShiftType(null);
                  setSchedulingStep(0);
                }}
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ 
          padding: '1.5rem', 
          overflowY: 'auto',
          flex: 1
        }}>
          {schedulingStep === 0 && (
            <>
              {/* Employee Info */}
              {filters.employeeId && (
          <div style={{ 
            marginBottom: '2rem', 
            padding: '1rem', 
            backgroundColor: theme === 'g90' || theme === 'g100' ? '#393939' : '#f4f4f4',
            borderRadius: '4px',
            borderLeft: '3px solid #9fb746'
          }}>
            <h4 style={{ 
              marginBottom: '0.5rem', 
              fontSize: '1rem', 
              fontWeight: '500',
              color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616'
            }}>
              Zaposleni
            </h4>
            <p style={{ 
              margin: 0, 
              fontSize: '0.95rem',
              color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252'
            }}>
              {employees.find(e => e.id === filters.employeeId)?.fullName || 
               employees.find(e => e.id === filters.employeeId)?.label || 'N/A'}
            </p>
          </div>
        )}

        {/* Selected Dates */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ 
            marginBottom: '0.75rem', 
            fontSize: '1rem', 
            fontWeight: '500',
            color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616'
          }}>
            Izabrani datumi
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {selectedDates.map((date, idx) => (
              <Tag key={idx} type="blue" size="md">
                {new Date(date).toLocaleDateString('en-GB', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric' 
                }).replace(/\//g, '.')}
              </Tag>
            ))}
          </div>
        </div>

              {/* Area Selection */}
              <div style={{ marginBottom: '1.5rem', display: 'inline-block', width: selectedArea ? '48%' : '100%', marginRight: selectedArea ? '2%' : '0' }}>
                <style>{`
                  ${theme === 'g90' || theme === 'g100' ? `
                    .scheduling-panel .cds--label { color: #f4f4f4 !important; }
                    .scheduling-panel .cds--list-box { background-color: #262626 !important; }
                    .scheduling-panel .cds--list-box__field { background-color: #262626 !important; color: #f4f4f4 !important; }
                    .scheduling-panel .cds--list-box__menu { background-color: #262626 !important; }
                    .scheduling-panel .cds--list-box__menu-item { background-color: #262626 !important; color: #f4f4f4 !important; }
                    .scheduling-panel .cds--list-box__menu-item:hover { background-color: #393939 !important; }
                    .scheduling-panel .cds--list-box__menu-item__option { color: #f4f4f4 !important; }
                    .scheduling-panel .cds--list-box__label { color: #f4f4f4 !important; }
                    .scheduling-panel span.cds--list-box__label { color: #f4f4f4 !important; }
                    #area-dropdown .cds--list-box__label { color: #f4f4f4 !important; }
                    #shift-type-dropdown .cds--list-box__label { color: #f4f4f4 !important; }
                  ` : ''}
                `}</style>
                <Dropdown
                  id="area-dropdown"
                  titleText="Područje"
                  label="Izaberite područje"
                  items={areas}
                  itemToString={(item) => (item ? item.label : '')}
                  selectedItem={selectedArea ? areas.find(a => a.id === selectedArea) || { id: selectedArea, label: selectedArea } : null}
                  onChange={({ selectedItem }) => {
                    setSelectedArea(selectedItem?.id || null);
                    setSelectedShiftType(null); // Reset shift type when area changes
                  }}
                />
              </div>

              {/* Shift Type Dropdown - only show when area is selected */}
              {selectedArea && (
                <div style={{ display: 'inline-block', width: '50%', verticalAlign: 'top' }}>
                  <Dropdown
                    id="shift-type-dropdown"
                    titleText="Tip smene"
                    label="Izaberite tip smene"
                    items={(shiftTypes[areas.find(a => a.id === selectedArea)?.label] || []).map(shift => ({
                      id: shift.id,
                      label: `${shift.name} (${shift.startTime} - ${shift.formattedEndTime})`,
                      name: shift.name,
                      startTime: shift.startTime,
                      endTime: shift.formattedEndTime
                    }))}
                    itemToString={(item) => (item ? item.label : '')}
                    selectedItem={(shiftTypes[areas.find(a => a.id === selectedArea)?.label] || []).map(shift => ({ 
                      id: shift.id, 
                      label: `${shift.name} (${shift.startTime} - ${shift.formattedEndTime})` 
                    })).find(s => s.id === selectedShiftType) || null}
                    onChange={({ selectedItem }) => setSelectedShiftType(selectedItem?.id || null)}
                  />
                </div>
              )}
            </>
          )}

          {/* Creating Shifts Progress */}
          {schedulingStep === 1 && (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              {isCreatingShifts ? (
                <>
                  <ProgressBar label="Kreiranje smena..." helperText="Molimo sačekajte..." />
                  <p style={{ 
                    marginTop: '1rem', 
                    color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252'
                  }}>
                    Zakazivanje smena u toku...
                  </p>
                </>
              ) : (
                <>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    margin: '0 auto 1rem',
                    borderRadius: '50%',
                    backgroundColor: '#9fb746',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Checkmark size={32} style={{ fill: 'white' }} />
                  </div>
                  <h4 style={{ 
                    margin: '0 0 0.5rem 0', 
                    fontSize: '1.1rem', 
                    fontWeight: '500',
                    color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616'
                  }}>
                    Smene uspešno zakazane!
                  </h4>
                  <p style={{ 
                    margin: 0, 
                    color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252'
                  }}>
                    Kreirano je {selectedDates.length} {selectedDates.length === 1 ? 'smena' : 'smena'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {schedulingStep === 0 && (
          <div style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--cds-border-subtle)',
            display: 'flex',
            gap: '1rem'
          }}>
            <Button
              kind="secondary"
              onClick={() => {
                setIsSchedulingMode(false);
                setSelectedShiftType(null);
                setSchedulingStep(0);
              }}
              style={{ flex: 1 }}
            >
              Otkaži
            </Button>
            <Button
              kind="primary"
              onClick={handleScheduleShifts}
              disabled={!selectedShiftType || isCreatingShifts}
              style={{ 
                flex: 1, 
                backgroundColor: '#9fb746', 
                borderColor: '#9fb746'
              }}
            >
              Zakaži smene
            </Button>
          </div>
        )}
      </div>
      </>,
      document.body
    )}

    {/* Delete Confirmation Modal */}
    {deleteModalOpen && ReactDOM.createPortal(
      <>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
            backdropFilter: 'blur(2px)'
          }}
          onClick={() => setDeleteModalOpen(false)}
        />
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: theme === 'g90' || theme === 'g100' ? '#262626' : '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
          zIndex: 9999,
          maxWidth: '500px',
          width: 'calc(100% - 2rem)',
          padding: '1.5rem'
        }}>
          <h4 style={{ 
            margin: '0 0 1rem 0', 
            fontSize: '1.25rem', 
            fontWeight: '500', 
            color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616' 
          }}>
            Brisanje smene
          </h4>
          <p style={{ 
            margin: '0 0 1.5rem 0', 
            color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252' 
          }}>
            Da li ste sigurni da želite da obrišete ovu smenu?
          </p>
          {shiftToDelete && (
            <div style={{
              backgroundColor: theme === 'g90' || theme === 'g100' ? '#393939' : '#f4f4f4',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ 
                  margin: '0 0 0.25rem 0', 
                  color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  Područje
                </p>
                <p style={{ 
                  margin: 0, 
                  color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616', 
                  fontWeight: '500',
                  fontSize: '0.875rem'
                }}>
                  {shiftToDelete.area}
                </p>
              </div>
              
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ 
                  margin: '0 0 0.25rem 0', 
                  color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  Datum
                </p>
                <p style={{ 
                  margin: 0, 
                  color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616',
                  fontSize: '0.875rem'
                }}>
                  {new Date(shiftToDelete.startTime).toLocaleDateString('sr-Latn-RS', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ 
                  margin: '0 0 0.25rem 0', 
                  color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  Vreme
                </p>
                <p style={{ 
                  margin: 0, 
                  color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616',
                  fontSize: '0.875rem'
                }}>
                  {formatTime(shiftToDelete.startTime)} - {formatTime(shiftToDelete.endTime)}
                </p>
              </div>
              
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ 
                  margin: '0 0 0.25rem 0', 
                  color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  Tip smene
                </p>
                <p style={{ 
                  margin: 0, 
                  color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616',
                  fontSize: '0.875rem'
                }}>
                  {shiftToDelete.name || 'N/A'}
                </p>
              </div>
              
              <div style={{ marginBottom: '0.75rem' }}>
                <p style={{ 
                  margin: '0 0 0.25rem 0', 
                  color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  Zaposleni
                </p>
                <p style={{ 
                  margin: 0, 
                  color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616',
                  fontSize: '0.875rem'
                }}>
                  {shiftToDelete.employeeFullName || 'N/A'}
                </p>
              </div>
              
              <div>
                <p style={{ 
                  margin: '0 0 0.25rem 0', 
                  color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}>
                  Status
                </p>
                <p style={{ 
                  margin: 0, 
                  color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616',
                  fontSize: '0.875rem'
                }}>
                  {translateStatus(shiftToDelete.status)}
                </p>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button
              kind="secondary"
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeletingShift}
              style={{ flex: 1 }}
            >
              Otkaži
            </Button>
            <Button
              kind="danger"
              onClick={handleDeleteShift}
              disabled={isDeletingShift}
              style={{ flex: 1 }}
            >
              {isDeletingShift ? 'Brisanje...' : 'Obriši'}
            </Button>
          </div>
        </div>
      </>,
      document.body
    )}

    {/* Notifications */}
    {notifications.length > 0 && ReactDOM.createPortal(
      <div
        data-carbon-theme={theme}
        style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 10001,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          maxWidth: '400px',
          width: 'calc(100% - 2rem)'
        }}
      >
        {notifications.map((notification) => (
          <div
            key={notification.id}
            style={{
              animation: notification.isExiting
                ? 'fadeOut 0.3s ease-out forwards'
                : 'fadeIn 0.3s ease-out',
              transformOrigin: 'top right'
            }}
          >
            <InlineNotification
              kind={notification.kind}
              title={notification.title}
              subtitle={notification.subtitle}
              hideCloseButton={false}
              onCloseButtonClick={() => {
                setNotifications(prev =>
                  prev.map(n =>
                    n.id === notification.id ? { ...n, isExiting: true } : n
                  )
                );
                setTimeout(() => {
                  setNotifications(prev => prev.filter(n => n.id !== notification.id));
                }, 300);
              }}
            />
          </div>
        ))}
      </div>,
      document.body
    )}

    {/* Weekly Report Side Panel */}
    {isReportPanelOpen && ReactDOM.createPortal(
      <>
        <div
          className="report-panel-overlay"
          onClick={closeReportPanel}
        />
        <Theme theme={theme}>
          <div className="report-side-panel">
            <div className="report-panel-header">
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: '500',
                  color: theme === 'g90' || theme === 'g100' ? '#f4f4f4' : '#161616'
                }}>
                  {reportType === 'monthly' ? 'Mesečni izveštaj' : 'Nedeljni izveštaj'}
                </h3>
                <p style={{
                  margin: '0.25rem 0 0 0',
                  fontSize: '0.875rem',
                  color: theme === 'g90' || theme === 'g100' ? '#c6c6c6' : '#525252'
                }}>
                  Generišite PDF izveštaj za izabrani period
                </p>
              </div>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Close}
                iconDescription="Zatvori"
                onClick={closeReportPanel}
              />
            </div>

            <div className="report-panel-content">
              {reportError && (
                <InlineNotification
                  kind="error"
                  title="Greška"
                  subtitle={reportError}
                  onCloseButtonClick={() => setReportError(null)}
                  hideCloseButton={false}
                  lowContrast
                />
              )}

              {/* Report type selection */}
              <div className="report-form-group">
                <label className="report-form-label">Tip izveštaja</label>
                <RadioButtonGroup
                  name="report-type"
                  valueSelected={reportType}
                  onChange={(value) => setReportType(value)}
                  orientation="horizontal"
                >
                  <RadioButton labelText="Nedeljni" value="weekly" id="report-type-weekly" />
                  <RadioButton labelText="Mesečni" value="monthly" id="report-type-monthly" />
                </RadioButtonGroup>
              </div>

              {/* Period info */}
              <div className="report-week-info">
                <Calendar size={20} />
                <div>
                  <span className="report-week-label">
                    {reportType === 'monthly' ? 'Mesec' : 'Nedelja'}
                  </span>
                  <span className="report-week-value">
                    {reportType === 'monthly'
                      ? (() => {
                          const monthStart = getMonthStart(currentDate);
                          return monthStart.toLocaleDateString('sr-Latn-RS', { month: 'long', year: 'numeric' });
                        })()
                      : (() => {
                          const weekStart = getWeekStart(currentDate);
                          const weekEnd = new Date(weekStart);
                          weekEnd.setDate(weekStart.getDate() + 6);
                          return `${weekStart.toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${weekEnd.toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                        })()}
                  </span>
                </div>
              </div>

              {/* Area selection (optional) */}
              <div className="report-form-group">
                <label className="report-form-label">Područje (opciono)</label>
                <Dropdown
                  id="report-area-filter"
                  titleText=""
                  label="Sva područja"
                  items={[{ id: '', label: 'Sva područja' }, ...areas]}
                  itemToString={(item) => (item ? item.label : '')}
                  selectedItem={reportAreaId ? areas.find(a => a.id === reportAreaId) : { id: '', label: 'Sva područja' }}
                  onChange={({ selectedItem }) => setReportAreaId(selectedItem ? selectedItem.id : '')}
                />
                <p className="report-form-helper">
                  Ako ne izaberete područje, izveštaj će obuhvatiti sva područja.
                </p>
              </div>

              <div className="report-panel-actions">
                <Button
                  kind="secondary"
                  onClick={closeReportPanel}
                  disabled={isGeneratingReport}
                  style={{ flex: 1 }}
                >
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  renderIcon={Download}
                  onClick={handleGenerateReport}
                  disabled={isGeneratingReport}
                  style={{ flex: 1 }}
                >
                  {isGeneratingReport ? 'Generisanje...' : 'Generiši'}
                </Button>
              </div>
            </div>
          </div>
        </Theme>
      </>,
      document.body
    )}

    {/* Day Details Right Side Bar */}
    {selectedDay && ReactDOM.createPortal(
      <>
        <div
          className="admin-shift-overlay"
          onClick={() => setSelectedDay(null)}
        />
        <Theme theme={theme}>
          <div className={`admin-shift-sidebar ${theme === 'g90' || theme === 'g100' ? 'admin-shift-sidebar--dark' : ''}`}>
            <div className="admin-shift-sidebar__header">
              <div className="admin-shift-sidebar__title">
                <button
                type="button"
                className="admin-shift-sidebar__back"
                onClick={() => setSelectedDay(null)}
                aria-label="Nazad na mesečni prikaz"
              >
                <ChevronLeft size={20} />
              </button>
              <div>
                <h3>{formatDateLong(selectedDay.date)}</h3>
                <p>
                  {selectedDay.shifts.length}{' '}
                  {selectedDay.shifts.length === 1 ? 'smena' : 'smena'}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="admin-shift-sidebar__close"
              onClick={() => setSelectedDay(null)}
              aria-label="Zatvori"
            >
              <Close size={20} />
            </button>
          </div>

          <div className="admin-shift-sidebar__content">
            {selectedDay.shifts.length === 0 ? (
              <div className="no-shifts-message">
                <Time size={32} />
                <p>Nema smena za ovaj dan</p>
              </div>
            ) : (
              <div className="admin-day-details-list">
                {selectedDay.shifts.map((shift, idx) => (
                  <div key={idx} className="admin-day-detail-card">
                    <div className="admin-day-detail-card__header">
                      <h4>{shift.area}</h4>
                      <button
                        className="shift-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShiftToDelete(shift);
                          setDeleteModalOpen(true);
                        }}
                        title="Obriši smenu"
                      >
                        <TrashCan size={16} />
                      </button>
                    </div>
                    <div className="admin-day-detail-card__row">
                      <Time size={16} />
                      <span className="admin-day-detail-card__label">Vreme:</span>
                      <span className="admin-day-detail-card__value">
                        {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                      </span>
                    </div>
                    <div className="admin-day-detail-card__row admin-day-detail-card__row--employee">
                      <User size={16} />
                      <span className="admin-day-detail-card__label">Zaposleni:</span>
                      <span className="admin-day-detail-card__value">{shift.employeeFullName}</span>
                      <div className="admin-day-detail-card__role-tag">
                        <Tag size="sm" type="outline">{translateRole(shift.employeeRole)}</Tag>
                      </div>
                    </div>
                    <div className="admin-day-detail-card__row">
                      <Location size={16} />
                      <span className="admin-day-detail-card__label">Tip smene:</span>
                      <span className="admin-day-detail-card__value">{shift.name}</span>
                    </div>
                    {shift.description && (
                      <div className="admin-day-detail-card__row admin-day-detail-card__description">
                        <span className="admin-day-detail-card__label">Opis:</span>
                        <span className="admin-day-detail-card__value">{shift.description}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </Theme>
      </>,
      document.body
    )}
    </>
  );
};

export default AdminShiftCalendar;
